import {
    DirectionsRenderer,
    GoogleMap,
    Polyline,
    TrafficLayer,
    useJsApiLoader
} from "@react-google-maps/api";
import dayjs from "dayjs";
import { get, ref } from "firebase/database";
import {
    Calendar,
    ChevronUp,
    Clock,
    LocateFixed,
    Map as MapIcon,
    MapPin,
    MessageCircle,
    Phone,
    Plane,
    Users
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { AdvancedMarker } from "../components/map/AdvancedMarker";
import { Button } from "../components/ui/button";
import { database } from "../firebase/firebase";
import LoadingScreen from "../utils/LoadingScreen";

const containerStyle = {
    width: "100%",
    height: "100%",
};

const defaultCenter = {
    lat: -27.59487, // Florianópolis center fallback
    lng: -48.54822,
};

type TripPhase = "IDLE" | "TO_PICKUP" | "IN_TRIP" | "COMPLETED";

// Define libraries as a static constant to prevent re-renders
const LIBRARIES: ("places" | "marker" | "geometry")[] = ["places", "marker", "geometry"];

// Define Map IDs as static constant
const MAP_IDS = [process.env.REACT_APP_GOOGLE_MAPS_ID || ""];

export default function DriverVoucher() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [booking, setBooking] = useState<any>(null);
    const [directionsResponse, setDirectionsResponse] =
        useState<google.maps.DirectionsResult | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);

    // States
    const [tripPhase, setTripPhase] = useState<TripPhase>("IDLE");
    const [driverLocation, setDriverLocation] =
        useState<google.maps.LatLngLiteral | null>(null);
    const [heading, setHeading] = useState<number>(0);
    const [speed, setSpeed] = useState<number | null>(null);
    const [sheetExpanded, setSheetExpanded] = useState(false);
    const [isAutoCentering, setIsAutoCentering] = useState(true);

    // Trip Stats
    const [remainingDistance, setRemainingDistance] = useState<string>("");
    const [remainingTime, setRemainingTime] = useState<string>("");
    const [eta, setEta] = useState<string>("");
    const [remainingPath, setRemainingPath] = useState<google.maps.LatLng[]>([]);

    const isAutoCenteringRef = useRef(isAutoCentering);

    // Sync ref with state
    useEffect(() => {
        isAutoCenteringRef.current = isAutoCentering;
    }, [isAutoCentering]);

    const headingRef = useRef(heading);
    useEffect(() => {
        headingRef.current = heading;
    }, [heading]);

    const mapRef = useRef<google.maps.Map | null>(null);

    const watchIdRef = useRef<number | null>(null);

    // Load Google Maps API with Map ID for Vector Maps
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
        mapIds: MAP_IDS,
        libraries: LIBRARIES,
    });

    // Fetch Booking Data
    useEffect(() => {
        if (!id) return;
        get(ref(database, `booking/${id}`)).then((snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const safeData = {
                    name: data.name,
                    phone: data.phone,
                    passengersQty: data.passengersQty,
                    origin: data.origin,
                    destination: data.destination,
                    date: data.date,
                    flight: data.flight,
                    passenger2: data.passenger2,
                    passenger3: data.passenger3,
                    passenger4: data.passenger4,
                };
                setBooking(safeData);
            } else {
                navigate("/");
            }
        });
    }, [id, navigate]);

    // Route Calculation Logic
    const calculateRoute = useCallback(async () => {
        if (!booking || !isLoaded) return;

        const directionsService = new google.maps.DirectionsService();
        let origin: string | google.maps.LatLngLiteral = booking.origin;
        let destination: string | google.maps.LatLngLiteral = booking.destination;

        if (tripPhase === "TO_PICKUP" && driverLocation) {
            origin = driverLocation;
            destination = booking.origin;
        } else if (tripPhase === "IN_TRIP") {
            origin = booking.origin;
            destination = booking.destination;
        }

        try {
            const result = await directionsService.route({
                origin: origin,
                destination: destination,
                travelMode: google.maps.TravelMode.DRIVING,
                drivingOptions: {
                    departureTime: new Date(),
                    trafficModel: google.maps.TrafficModel.BEST_GUESS
                }
            });
            setDirectionsResponse(result);

            // Update stats based on first leg
            if (result.routes[0] && result.routes[0].legs[0]) {
                const leg = result.routes[0].legs[0];
                setRemainingDistance(leg.distance?.text || "");
                setRemainingTime(leg.duration?.text || "");
                // Calculate ETA time
                const durationSecs = leg.duration?.value || 0;
                setEta(dayjs().add(durationSecs, 'second').format("HH:mm"));
            }

        } catch (error) {
            console.error("Error calculating route:", error);
        }
    }, [booking, isLoaded, tripPhase, driverLocation]);

    // Recalculate route when phase changes
    useEffect(() => {
        if (booking && isLoaded) {
            calculateRoute();
        }
    }, [booking, isLoaded, calculateRoute, tripPhase]);

    // Geolocation Tracking (Real GPS)
    useEffect(() => {


        const startTracking = () => {
            if (navigator.geolocation) {
                watchIdRef.current = navigator.geolocation.watchPosition(
                    (position) => {
                        const newPos = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };
                        setDriverLocation(newPos);

                        // Should verify heading validity
                        let newHeading = headingRef.current;
                        if (position.coords.heading !== null && !isNaN(position.coords.heading)) {
                            newHeading = position.coords.heading;
                            setHeading(newHeading);
                        }

                        if (position.coords.speed !== null && !isNaN(position.coords.speed)) {
                            setSpeed(position.coords.speed * 3.6);
                        } else {
                            setSpeed(0);
                        }

                        if (tripPhase !== "IDLE" && tripPhase !== "COMPLETED" && mapRef.current && isAutoCentering) {

                            // Calculate offset center to push car to bottom of screen (Look Ahead)
                            let center = newPos;
                            if (window.google?.maps?.geometry) {
                                center = window.google.maps.geometry.spherical.computeOffset(newPos, 40, newHeading || 0).toJSON();
                            }

                            mapRef.current.moveCamera({
                                center: center,
                                heading: newHeading || 0,
                                zoom: 20
                            });
                        }
                    },
                    (error) => console.error("Error watching position:", error),
                    {
                        enableHighAccuracy: true,
                        maximumAge: 0,
                        timeout: 5000
                    }
                );
            }
        };

        if (tripPhase !== "IDLE" && tripPhase !== "COMPLETED") {
            startTracking();
        } else {
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;

                if (mapRef.current) {
                    mapRef.current.setTilt(0);
                    mapRef.current.setHeading(0);
                    mapRef.current.setZoom(13);
                    setIsAutoCentering(true);
                }
            }
        }

        return () => {
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
            }
        };
    }, [tripPhase, isAutoCentering]); // Removed 'heading' from dep array to avoid loops, tracking logic manages it locally if needed, or state updates trigger re-render


    const onLoad = React.useCallback(function callback(map: google.maps.Map) {
        setMap(map);
        mapRef.current = map;
    }, []);

    const onUnmount = React.useCallback(function callback(map: google.maps.Map) {
        setMap(null);
        mapRef.current = null;
    }, []);

    useEffect(() => {
        if (!mapRef.current) return;
        const listener = mapRef.current.addListener("dragstart", () => {
            if (tripPhase !== "IDLE") {
                setIsAutoCentering(false);
            }
        });

        return () => {
            if (listener) google.maps.event.removeListener(listener);
        };
    }, [tripPhase]);

    // Calculate Remaining Path to show only future route
    useEffect(() => {
        if (!directionsResponse || !driverLocation || !window.google?.maps?.geometry) return;

        const route = directionsResponse.routes[0];
        if (!route || !route.overview_path) return;

        const path = route.overview_path;
        let minDistance = Infinity;
        let closestIndex = 0;

        // Find closest point on path to current location
        for (let i = 0; i < path.length; i++) {
            const dist = google.maps.geometry.spherical.computeDistanceBetween(driverLocation, path[i]);
            if (dist < minDistance) {
                minDistance = dist;
                closestIndex = i;
            }
        }

        // Slice path from closest point and prepend current location for smooth line
        const newPath = path.slice(closestIndex);
        setRemainingPath([new google.maps.LatLng(driverLocation), ...newPath]);

    }, [driverLocation, directionsResponse]);




    if (!booking) {
        return <LoadingScreen />;
    }

    const openGoogleMaps = () => {
        const dest =
            tripPhase === "TO_PICKUP" ? booking.origin : booking.destination;
        window.open(
            `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                dest
            )}`,
            "_blank"
        );
    };

    const advancePhase = () => {
        if (tripPhase === "IDLE") {
            setTripPhase("TO_PICKUP");
            setSheetExpanded(false);
            setIsAutoCentering(true);
        }
        else if (tripPhase === "TO_PICKUP") {
            setTripPhase("IN_TRIP");
            setIsAutoCentering(true);
        }
        else if (tripPhase === "IN_TRIP") {
            setTripPhase("IDLE");
            setSheetExpanded(true);
            setIsAutoCentering(true);
        }
    };

    const recenterMap = () => {
        setIsAutoCentering(true);
        if (mapRef.current && driverLocation) {
            const currentZoom = mapRef.current.getZoom() || 18;

            let center = driverLocation;
            if (window.google?.maps?.geometry) {
                center = window.google.maps.geometry.spherical.computeOffset(driverLocation, 40, heading).toJSON();
            }

            mapRef.current.moveCamera({ center: center, heading: heading, tilt: 67.5, zoom: currentZoom });
        }
    };

    const getPhaseButtonText = () => {
        switch (tripPhase) {
            case "IDLE": return "Buscar Passageiros";
            case "TO_PICKUP": return "Ir ao Destino";
            case "IN_TRIP": return "Encerrar Viagem";
            default: return "Iniciar";
        }
    };

    const toggleSheet = () => setSheetExpanded(!sheetExpanded);
    const isSheetContentVisible = sheetExpanded || tripPhase === "IDLE";

    return (
        <div className="flex h-[100dvh] flex-col bg-slate-50 relative overflow-hidden">
            {/* Map Section */}
            <div className={`absolute top-0 left-0 right-0 transition-all duration-300 ${isSheetContentVisible ? 'bottom-[60vh]' : 'bottom-[220px]'}`}>
                {isLoaded ? (
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={driverLocation || defaultCenter}
                        zoom={13}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{
                            disableDefaultUI: true,
                            zoomControl: false,
                            mapTypeControl: false,
                            clickableIcons: false,
                            rotateControl: false,
                            streetViewControl: false,
                            fullscreenControl: false,
                            mapId: process.env.REACT_APP_GOOGLE_MAPS_ID
                        }}
                    >
                        <TrafficLayer />

                        {directionsResponse && (
                            <DirectionsRenderer
                                directions={directionsResponse}
                                options={{
                                    suppressMarkers: tripPhase !== "IDLE",
                                    suppressPolylines: tripPhase !== "IDLE", // Hide original line during navigation
                                    preserveViewport: tripPhase !== "IDLE",
                                    polylineOptions: {
                                        strokeColor: tripPhase === "TO_PICKUP" ? "#64748b" : "#3b82f6",
                                        strokeWeight: 6,
                                        strokeOpacity: 0.8,
                                        zIndex: 1,
                                    },
                                }}
                            />
                        )}

                        {/* Render Remaining Path Custom Polyline */}
                        {(tripPhase === "TO_PICKUP" || tripPhase === "IN_TRIP") && remainingPath.length > 0 && (
                            <Polyline
                                path={remainingPath}
                                options={{
                                    strokeColor: "#3b82f6", // Azul (blue-500) para viagem
                                    strokeWeight: 20, // Grossura da linha
                                    strokeOpacity: 1.0, // Opacidade (1.0 = sólido)
                                    zIndex: 10,
                                }}
                            />
                        )}



                        {driverLocation && (
                            <AdvancedMarker
                                map={map}
                                position={driverLocation}
                                rotation={isAutoCentering ? 0 : heading} // If auto-centering (navigation mode), map rotates so car stays up (0). If manual, car rotates.
                                zIndex={999}
                            >
                                {/* Car Icon (SVG) */}
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transform: 'rotate(0deg)' // handled by parent wrapper but ensuring clean start
                                }}>
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2L4.5 20.29C4.24 20.93 4.89 21.56 5.53 21.24L12 18L18.47 21.24C19.11 21.56 19.76 20.93 19.5 20.29L12 2Z" fill="#ef4444" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            </AdvancedMarker>
                        )}

                        {tripPhase === "TO_PICKUP" && directionsResponse && (
                            <AdvancedMarker
                                map={map}
                                position={directionsResponse.routes[0].legs[0].end_location}
                            >
                                <div className="bg-emerald-500 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center border-2 border-white shadow-md text-sm">
                                    P
                                </div>
                            </AdvancedMarker>
                        )}

                        {tripPhase === "IN_TRIP" && directionsResponse && (
                            <AdvancedMarker
                                map={map}
                                position={directionsResponse.routes[0].legs[0].end_location}
                            >
                                <div className="bg-red-500 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center border-2 border-white shadow-md text-sm">
                                    D
                                </div>
                            </AdvancedMarker>
                        )}

                    </GoogleMap>
                ) : (
                    <div className="flex h-full items-center justify-center text-slate-400 bg-slate-200">
                        Carregando Mapa...
                    </div>
                )}

                {/* Recenter Button - Bottom Right above Sheet */}
                {(tripPhase === "TO_PICKUP" || tripPhase === "IN_TRIP") && !isAutoCentering && (
                    <button
                        onClick={recenterMap}
                        className="absolute bottom-8 right-4 z-50 bg-slate-900/90 backdrop-blur-md p-3 rounded-full shadow-lg text-white border border-slate-700 hover:bg-slate-800 transition-colors flex items-center gap-2"
                    >
                        <LocateFixed className="h-6 w-6" />
                        <span>Recentralizar</span>
                    </button>
                )}

                {/* Navigation Info Overlay (Top Right FIXED) */}
                {(tripPhase === "TO_PICKUP" || tripPhase === "IN_TRIP") && (
                    <div className="absolute top-4 right-4 z-50 flex flex-col items-end gap-2 pointer-events-none">
                        {/* Trip Stats (Bubble) */}
                        <div className="bg-slate-900/90 backdrop-blur-md px-3 py-2 rounded-lg shadow-lg border border-slate-700 text-white min-w-[120px]">
                            <div className="flex items-baseline justify-between mb-1">
                                <span className="text-xl font-bold text-green-400">{eta}</span>
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Chegada</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs font-medium opacity-90">
                                <span>{remainingTime}</span>
                                <span className="w-1 h-1 rounded-full bg-slate-500"></span>
                                <span>{remainingDistance}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Speedometer (Bottom Left - Above Sheet) */}
                {(tripPhase === "TO_PICKUP" || tripPhase === "IN_TRIP") && (
                    <div className="absolute bottom-6 left-4 z-50 flex flex-col items-center justify-center pointer-events-none">
                        <div className="bg-slate-900/90 backdrop-blur-md h-16 w-16 rounded-full shadow-lg border-4 border-slate-700 flex flex-col items-center justify-center">
                            <span className="text-2xl font-black text-white leading-none">
                                {Math.round(speed || 0)}
                            </span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase">km/h</span>
                        </div>
                    </div>
                )}
            </div>


            {/* External Map Button */}
            {tripPhase === "IDLE" && (
                <button
                    onClick={openGoogleMaps}
                    className="absolute top-4 right-4 z-10 bg-white p-3 rounded-full shadow-lg text-slate-700 hover:text-blue-600 border border-slate-100"
                >
                    <MapIcon className="h-6 w-6" />
                </button>
            )}


            {/* Bottom Sheet Closing Div */}
            <div
                className={`absolute bottom-0 left-0 right-0 bg-white shadow-[0_-5px_25px_rgba(0,0,0,0.15)] rounded-t-3xl z-40 transition-all duration-300 ease-in-out flex flex-col ${isSheetContentVisible ? 'h-[60dvh]' : 'h-auto pb-8 pt-2'
                    }`}
                style={{ minHeight: isSheetContentVisible ? '60dvh' : '220px' }}
            >
                <div
                    onClick={toggleSheet}
                    className="w-full flex items-center justify-center pt-3 pb-2 cursor-pointer active:opacity-70 bg-white rounded-t-3xl"
                >
                    <div className="h-1.5 w-12 rounded-full bg-slate-300 mb-1"></div>
                </div>

                <div className={`flex-1 px-6 overflow-y-auto no-scrollbar transition-opacity duration-200 ${isSheetContentVisible ? 'opacity-100' : 'opacity-100'}`}>

                    {/* Header */}
                    <div className="mb-4 text-center">
                        {tripPhase === "TO_PICKUP" && (
                            <div className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wide mb-2 animate-pulse">
                                Indo para Embarque
                            </div>
                        )}
                        {tripPhase === "IN_TRIP" && (
                            <div className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wide mb-2 animate-pulse">
                                Em Rota
                            </div>
                        )}
                        <h1 className="text-xl font-bold text-slate-900 leading-tight">
                            {booking.name}
                        </h1>
                        <p className="text-sm text-slate-500 flex items-center justify-center gap-1 mt-1">
                            {tripPhase === "TO_PICKUP"
                                ? `Buscar: ${booking.origin}`
                                : `Levar: ${booking.destination}`}
                            {!isSheetContentVisible && <ChevronUp className="h-4 w-4" />}
                        </p>
                    </div>

                    {/* Expandable Details */}
                    <div className={`space-y-4 ${isSheetContentVisible ? 'block' : 'hidden'}`}>
                        {/* Route Box */}
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="mt-1 h-3 w-3 rounded-full bg-slate-400 shrink-0" />
                                <div>
                                    <p className="text-xs font-bold uppercase text-slate-400">Origem</p>
                                    <p className="text-sm font-medium text-slate-900">{booking.origin}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="mt-0.5 h-4 w-4 text-red-500 shrink-0" />
                                <div>
                                    <p className="text-xs font-bold uppercase text-slate-400">Destino</p>
                                    <p className="text-sm font-medium text-slate-900">{booking.destination}</p>
                                </div>
                            </div>
                        </div>

                        {/* Logistics Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                                <div className="flex items-center gap-2 mb-1 text-slate-500">
                                    <Calendar className="h-4 w-4" />
                                    <span className="text-xs font-semibold">Data</span>
                                </div>
                                <p className="text-sm font-bold text-slate-900">
                                    {dayjs(booking.date).format("DD/MM")}
                                </p>
                            </div>

                            <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                                <div className="flex items-center gap-2 mb-1 text-slate-500">
                                    <Clock className="h-4 w-4" />
                                    <span className="text-xs font-semibold">Horário</span>
                                </div>
                                <p className="text-sm font-bold text-slate-900">
                                    {dayjs(booking.date).format("HH:mm")}
                                </p>
                            </div>

                            <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                                <div className="flex items-center gap-2 mb-1 text-slate-500">
                                    <Users className="h-4 w-4" />
                                    <span className="text-xs font-semibold">Pax</span>
                                </div>
                                <p className="text-sm font-bold text-slate-900">
                                    {booking.passengersQty}
                                </p>
                            </div>

                            {booking.flight && (
                                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                                    <div className="flex items-center gap-2 mb-1 text-slate-500">
                                        <Plane className="h-4 w-4" />
                                        <span className="text-xs font-semibold">Voo</span>
                                    </div>
                                    <p className="text-sm font-bold text-slate-900 truncate">
                                        {booking.flight}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="h-4"></div>
                    </div>
                </div>

                {/* Action Footer */}
                <div className="px-6 bg-white flex flex-col gap-3">
                    <Button
                        onClick={advancePhase}
                        className={`w-full h-12 text-lg font-bold shadow-lg transition-colors backdrop-blur-md ${tripPhase === "IN_TRIP"
                            ? "bg-red-500 hover:bg-red-600 text-white border border-red-500"
                            : "bg-slate-900/90 hover:bg-slate-800 text-white border border-slate-700"
                            }`}
                    >
                        {getPhaseButtonText()}
                    </Button>

                    <div className={`${isSheetContentVisible ? 'block' : 'hidden'} space-y-3`}>
                        <div className="grid grid-cols-2 gap-3">
                            <Button
                                onClick={() => (window.location.href = `tel:${booking.phone}`)}
                                variant="outline"
                                className="h-10 border-slate-200"
                            >
                                <Phone className="h-4 w-4 mr-2" />
                                Ligar
                            </Button>
                            <Button
                                onClick={() =>
                                    window.open(`https://wa.me/${booking.phone}`, "_blank")
                                }
                                className="h-10 bg-emerald-500 hover:bg-emerald-600 text-white border-none"
                            >
                                <MessageCircle className="h-4 w-4 mr-2" />
                                WhatsApp
                            </Button>
                        </div>

                        <Button
                            onClick={openGoogleMaps}
                            variant="ghost"
                            className="h-10 text-slate-500 hover:bg-slate-50 hover:text-slate-900 w-full"
                        >
                            <MapIcon className="h-4 w-4 mr-2" />
                            Abrir no Google Maps
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
